"use client";
import React, { useState } from 'react';
import {
  TrendingUp,
  ShieldCheck,
  ChevronRight,
  Briefcase
} from 'lucide-react';
import Link from 'next/link';
import CalendlyLink from "@/components/layout/CalendlyLink";

const CapitalMarketsSection = ({ data }) => {
  // Defensive check for data
  if (!data?.models) return null;

  const modelKeys = Object.keys(data.models);
  const [activeTab, setActiveTab] = useState(modelKeys[0] || 'FTE');

  return (
    <section
      className="py-24 bg-[#00173F] text-white overflow-hidden"
      aria-labelledby="capital-markets-title"
    >
      <div className="max-w-7xl mx-auto px-6">

        {/* Header Section */}
        <header className="mb-16">
          <h2 className="text-white font-bold tracking-widest uppercase text-sm mb-6 flex items-center gap-3">
            <span className="w-10 h-[2px] bg-white/40" aria-hidden="true"></span>
            {data.subTitle}
          </h2>
          <div className='flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-16'>
            <h3
              id="capital-markets-title"
              className="max-w-3xl text-4xl md:text-6xl font-extrabold tracking-tight text-white leading-[1.1]"
            >
              {data.title}
            </h3>
            <p className="max-w-xl text-lg text-white/70 leading-relaxed font-medium">
              {data.description}
            </p>
          </div>
        </header>

        {/* Interactive Tabs Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          {/* Tab Navigation Sidebar */}
          <nav
            className="lg:col-span-4 space-y-4"
            role="tablist"
            aria-label="Investment Model Selection"
          >
            {modelKeys.map((key) => (
              <button
                key={key}
                id={`tab-${key}`}
                role="tab"
                aria-selected={activeTab === key}
                aria-controls={`panel-${key}`}
                onClick={() => setActiveTab(key)}
                className={`w-full text-left p-6 rounded-2xl border transition-all duration-300 group outline-none focus-visible:ring-2 focus-visible:ring-white ${activeTab === key
                    ? 'bg-white border-white translate-x-2 shadow-2xl'
                    : 'bg-[#002A63]/50 border-white/10 hover:border-white/30 hover:bg-[#003680]'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-bold text-lg transition-colors ${activeTab === key ? 'text-[#00173F]' : 'text-white'
                    }`}>
                    {data.models[key].title}
                  </span>
                  <ChevronRight className={`w-5 h-5 transition-all duration-300 ${activeTab === key ? 'text-[#00173F] rotate-90' : 'text-white/40 group-hover:text-white'
                    }`} />
                </div>
              </button>
            ))}

            {/* CTA Card */}
            <div className="mt-10 p-8 bg-gradient-to-br from-[#002A63] to-[#00173F] rounded-2xl border border-white/10 shadow-xl">
              <p className="text-sm font-semibold text-white/90 mb-5 leading-snug">
                Ready to institutionalize your proprietary workflow?
              </p>
              <div className="w-full">
                <CalendlyLink bgColor="bg-white" />
              </div>
            </div>
          </nav>

          {/* Dynamic Content Display */}
          <article
            className="lg:col-span-8 bg-[#002A63] rounded-[2.5rem] p-8 md:p-14 border border-white/10 relative overflow-hidden shadow-2xl min-h-[500px]"
            role="tabpanel"
            id={`panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            {/* Background Decoration */}
            <Briefcase
              className="absolute -bottom-16 -right-16 w-80 h-80 text-white/5 pointer-events-none rotate-12"
              aria-hidden="true"
            />

            <div
              className="relative z-10 transition-opacity duration-500 ease-in-out"
              key={activeTab} // Triggers re-render for clean enter animation
            >
              <header className="mb-10">
                <h4 className="text-4xl font-extrabold text-white mb-2">
                  {data.models[activeTab].title}
                </h4>
                <p className="text-[#4F7AC2] font-bold text-xl tracking-wide">
                  {data.models[activeTab].subtitle}
                </p>
              </header>

              <p className="text-white/80 text-lg mb-12 max-w-2xl leading-relaxed">
                {data.models[activeTab].description}
              </p>

              <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-10 mb-12 list-none p-0">
                {data.models[activeTab].features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-4 group">
                    <div className="mt-1 bg-white/5 group-hover:bg-white transition-all duration-300 rounded-full p-1.5 border border-white/10 group-hover:border-white">
                      <ShieldCheck className="w-4 h-4 text-white group-hover:text-[#00173F]" aria-hidden="true" />
                    </div>
                    <span className="text-white/90 font-semibold tracking-tight leading-tight">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <footer className="pt-10 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-8">
                <div className="flex items-center gap-4">
                  <div className="p-3.5 bg-[#00173F] rounded-2xl shadow-inner border border-white/10" aria-hidden="true">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-sm text-white/70 italic max-w-[260px] leading-relaxed">
                    {data.models[activeTab].designedFor}
                  </p>
                </div>

                <Link href="/contact" className="w-full sm:w-auto" aria-label="Request consultation for capital markets services">
                  <button className="w-full sm:w-auto px-8 py-4 border-2 border-white/20 rounded-xl font-bold text-sm text-white hover:bg-white hover:text-[#00173F] hover:border-white transition-all duration-300 focus:ring-2 focus:ring-white">
                    Request Consultation
                  </button>
                </Link>
              </footer>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default CapitalMarketsSection;